import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    getDateFormat
} from "../../../../Apis/functions";
import { TABLES } from "../../../../Apis/Tables";
import CustomChip from "../../../../components/CustomChip";
import CustomTd from "../../../../components/table/CustomTd";
import CustomTh from "../../../../components/table/CustomTh";
import { SEARCH_MODEL } from "../../../../components/table/Model/TableModel";
import TableFunction from "../../../../components/table/TableFunction";
import { colors } from "../../../../enums/colors";
import { SimpleSearchWithId } from "../../../../reduxToolkit/Search/SearchApi";
import { renderMeals } from "../../Dining/ExtraMeal/Model/Models";

/**
 *
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * Custom React hook for managing the "My Orders" table logic.
 *
 * Handles fetching and displaying user order data, manages table state and pagination,
 * and formats the data for a custom table component.
 *
 * @function
 * @returns {Object} An object containing properties and functions for table rendering and control.
 */

const MyOrdersTableApp = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    /**
     * State to store order results, expects an object with 'content' (array) and 'totalElements' (number).
     * @type {[{ content: Array, totalElements: number }, Function]}
     */
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [userId, setUserId] = useState(sessionStorage.getItem('id'));
    const contractorId = sessionStorage.getItem('contractorId');
    const [searchElement, setSearchElement] = useState('')
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [modified, setModified] = useState(false);
    const [search, setSearch] = useState(false);
    const tableId = "tbl_mot_orders";
    const option = TABLES.FOODIE_USER_MENU_SELECTION;
    const searchId = "txt_mot_search_order"


    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={false} left={0} customWidth="150">{t('order_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('diner')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('cafeteria_serving_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('meal_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('menu_items')}</CustomTh>,
        ];
    };

    /**
     * Functional component for displaying a chip with custom legend and colors.
     * @param {Object} props
     * @param {string} props.legend - The text to display inside the chip.
     * @returns {React.ReactNode}
     */
    const MyOrdersChip = ({ legend }) => {
        return <CustomChip
            background={colors.secondary100}
            borderColor={colors.secondary150}
            legend={legend}
            fontColor={colors.secondary700}
        />
    };

    /**
     * Generates the body of the table based on the fetched results.
     * Each row displays relevant order information.
     *
     * @returns {{ body: Array<Array<React.ReactNode>>, data: Object }}
     */
    const getTbody = () => {
        const content = results?.content || [];
        if (content.length === 0) return { body: [], data: [] };

        let body = content.map((item) => {
            return [
                <CustomTd tableId={tableId} fixedXY={false} left={0} customWidth="150">
                    {getDateFormat(item?.createdAt, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth) || EMPTY_VALUES.DEFAULT }
                </CustomTd>,
                <CustomTd tableId={tableId}>{item?.cafeteriaName || EMPTY_VALUES.DEFAULT}</CustomTd>,
                <CustomTd tableId={tableId}>{getDateFormat(item?.servingDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth) || EMPTY_VALUES.DEFAULT}</CustomTd>,
                <CustomTd tableId={tableId}>
                    <MyOrdersChip legend={t(item?.mealTimeName) || EMPTY_VALUES.DEFAULT} />
                </CustomTd>,
                <CustomTd tableId={tableId} classes={"text-capitalize"}>
                    {renderMeals(item)}
                </CustomTd>

            ];
        });

        return {
            body,
            data: results
        };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: option,
            userId: userId,
            key:searchElement
        });

        dispatch(SimpleSearchWithId({ body })).then(
            ({
                 payload: {
                     data: { data, success },
                 },
             }) => {
                if (success) {
                    setResults(data);
                }
            },
        );
    }

    const {
        thead,
        tbody,
        pagination,
        page,
        rowsPerPage,
    } = TableFunction({
        data: results,
        getThead,
        getTbody,
        labelRowsPerPage: 'order_per_page',
        fixedPagination: false,
        hasContainer: true,
        searchElement,//New
        getData,//New
        modified,//New
        setModified,//New
    });

    return {
        navigate,
        supplierId: userId,
        thead,
        tbody,
        pagination,
        results,
        searchElement,
        setSearchElement,
        option,
        page,
        rowsPerPage,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        contractorId,
        setResults,
        searchId,
        tableId
    };
};

export default MyOrdersTableApp;
